import { Card, Table, Tag, Button, Empty, Typography } from 'antd';
import { WarningOutlined, ExclamationCircleOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';

const { Text } = Typography;

interface Part {
  id: string;
  displayId: string;
  name: string;
  currentStockLevel?: number | null;
  optimalStockLevel?: number | null;
  unit?: string | null;
}

interface PartsData {
  items: Part[];
}

interface LowStockPartsTableProps {
  data?: PartsData;
  loading?: boolean;
}

export function LowStockPartsTable({ data, loading }: LowStockPartsTableProps) {
  const navigate = useNavigate();

  const parts = data?.items || [];

  // 在庫不足の部品・消耗品をフィルタリング（在庫切れ + 在庫不足）
  const lowStockParts = parts
    .filter((p) => {
      const current = p.currentStockLevel ?? 0;
      const optimal = p.optimalStockLevel ?? 0;
      return current < optimal;
    })
    .sort((a, b) => {
      // 在庫切れを優先、次に不足率が高い順
      const currentA = a.currentStockLevel ?? 0;
      const currentB = b.currentStockLevel ?? 0;
      if (currentA === 0 && currentB > 0) return -1;
      if (currentB === 0 && currentA > 0) return 1;

      const optimalA = a.optimalStockLevel ?? 1;
      const optimalB = b.optimalStockLevel ?? 1;
      const rateA = currentA / optimalA;
      const rateB = currentB / optimalB;
      return rateA - rateB;
    })
    .slice(0, 5); // 上位5件

  const columns: ColumnsType<Part> = [
    {
      title: '状態',
      key: 'status',
      width: 100,
      render: (_, record) => {
        const current = record.currentStockLevel ?? 0;
        if (current === 0) {
          return <Tag icon={<ExclamationCircleOutlined />} color="error">在庫切れ</Tag>;
        }
        return <Tag icon={<WarningOutlined />} color="warning">在庫不足</Tag>;
      },
    },
    {
      title: '部品・消耗品ID',
      dataIndex: 'displayId',
      key: 'displayId',
      width: 100,
    },
    {
      title: '部品・消耗品名',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text, record) => (
        <a onClick={() => navigate(`/app/parts/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: '在庫数',
      key: 'stock',
      width: 150,
      render: (_, record) => {
        const current = record.currentStockLevel ?? 0;
        const optimal = record.optimalStockLevel ?? 0;
        const unit = record.unit || '個';
        return (
          <Text>
            <Text strong style={{ color: current === 0 ? '#ff4d4f' : '#faad14' }}>
              {current}
            </Text>
            {' / '}
            {optimal}
            {` ${unit}`}
          </Text>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<ShoppingCartOutlined />}
          onClick={() => navigate(`/app/purchase-orders/create?partId=${record.id}`)}
        >
          発注
        </Button>
      ),
    },
  ];

  return (
    <Card
      title={
        <span>
          <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
          在庫不足アラート
        </span>
      }
      extra={
        <Button type="link" onClick={() => navigate('/app/stock-dashboard')}>
          すべて見る
        </Button>
      }
    >
      {lowStockParts.length > 0 ? (
        <Table
          columns={columns}
          dataSource={lowStockParts}
          rowKey="id"
          pagination={false}
          size="small"
          loading={loading}
          scroll={{ x: 'max-content' }}
        />
      ) : (
        !loading && (
          <Empty
            description="在庫不足の部品・消耗品はありません"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )
      )}
    </Card>
  );
}
